import { Box, Chip, CircularProgress, Tab, Tabs, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { appointmentService, IBusinessEmployeeView, IBusinessView } from '../../../services';
import { AppointmentFinance } from '../appointmentFinance/AppointmentFinance';
import { Button } from '../../MUI/button/Button';
import { NewOrderModal } from '../../../../pages/business/pages/NewOrderModal';

type DatePreset = 'today' | 'week' | 'month' | 'all';

interface IOrderTicketsProps {
  /** Business context — obrigatório para modo business e para criar novas comandas */
  business?: IBusinessView;
  /** BusinessEmployee específico (modo employee) — filtra por este profissional */
  businessEmployee?: IBusinessEmployeeView;
  /** Refetch externo opcional */
  refetch?: () => void;
}

const getPresetRange = (preset: DatePreset): { start: Date; end: Date } => {
  const now = new Date();
  const start = new Date(now);
  const end = new Date(now);

  if (preset === 'today') {
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
  } else if (preset === 'week') {
    start.setDate(now.getDate() - now.getDay());
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
  } else if (preset === 'month') {
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
  } else {
    start.setFullYear(2000);
    end.setFullYear(2099);
  }
  return { start, end };
};

export const OrderTickets: React.FC<IOrderTicketsProps> = ({ business, businessEmployee }) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [preset, setPreset] = useState<DatePreset>('month');
  const [newOrderModalOpen, setNewOrderModalOpen] = useState(false);

  const isBusinessMode = !!business;

  const { start, end } = useMemo(() => getPresetRange(preset), [preset]);

  const queryKey = isBusinessMode
    ? ['appointments', business!.id, preset]
    : ['appointments', 'employee', businessEmployee?.id];

  const { data: appointments, refetch: refetchAppointments, isFetching } = useQuery({
    queryKey,
    queryFn: () => {
      if (isBusinessMode) {
        return preset === 'all'
          ? appointmentService.get(business!.id)
          : appointmentService.get(business!.id, undefined, start, end);
      }
      // Employee mode: filtra estritamente pelo businessEmployeeId
      if (businessEmployee?.id) {
        return appointmentService.get(undefined, businessEmployee.id);
      }
      return [];
    },
    staleTime: isBusinessMode ? (preset === 'all' ? 120_000 : 30_000) : 60_000,
  });

  const appointmentsPast = useMemo(() => {
    if (isBusinessMode) {
      return appointments?.filter(a => {
        if (!a.past || a.canceled) return false;
        const d = new Date(a.date);
        return d >= start && d <= end;
      }) || [];
    }
    return appointments?.filter(a => !a.canceled) || [];
  }, [appointments, start, end, isBusinessMode]);

  const pending = useMemo(() => appointmentsPast.filter(a => !a.completed), [appointmentsPast]);
  const completed = useMemo(() => appointmentsPast.filter(a => a.completed), [appointmentsPast]);

  const pendingRevenue = useMemo(() => pending.reduce((t, a) => t + (a.totalAmount || 0), 0), [pending]);
  const completedRevenue = useMemo(() => completed.reduce((t, a) => t + (a.totalAmount || 0), 0), [completed]);

  const presets: { key: DatePreset; label: string }[] = [
    { key: 'today', label: 'Hoje' },
    { key: 'week', label: 'Semana' },
    { key: 'month', label: 'Mês' },
    { key: 'all', label: 'Tudo' },
  ];

  return isFetching ? (
    <Box display='flex' justifyContent='center' py={6}><CircularProgress /></Box>
  ) : (
    <Box display='flex' flexDirection='column' gap={2}>
      {/* Nova Comanda button — only with business context */}
      {isBusinessMode && (
        <Box display='flex' justifyContent='flex-end'>
          <Button
            label='+ Nova Comanda'
            variant='contained'
            onClick={() => setNewOrderModalOpen(true)}
          />
        </Box>
      )}

      {/* Date preset buttons — only in business mode */}
      {isBusinessMode && (
        <ToggleButtonGroup
          value={preset}
          exclusive
          onChange={(_, value) => value && setPreset(value)}
          size='small'
          fullWidth
        >
          {presets.map(p => (
            <ToggleButton key={p.key} value={p.key}>
              {p.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}

      {/* Summary chips — only in business mode */}
      {isBusinessMode && (
        <Box display='flex' gap={1} flexWrap='wrap'>
          <Chip
            icon={<Typography variant='caption' sx={{ pl: 0.5 }}>⏳</Typography>}
            label={`${pending.length} pendente${pending.length !== 1 ? 's' : ''} · R$ ${pendingRevenue.toFixed(2)}`}
            color='warning'
            variant='outlined'
            sx={{ fontWeight: 600 }}
          />
          <Chip
            icon={<Typography variant='caption' sx={{ pl: 0.5 }}>✅</Typography>}
            label={`${completed.length} finalizado${completed.length !== 1 ? 's' : ''} · R$ ${completedRevenue.toFixed(2)}`}
            color='success'
            variant='outlined'
            sx={{ fontWeight: 600 }}
          />
        </Box>
      )}

      <Tabs value={tabIndex} onChange={(_, i) => setTabIndex(i)} variant='fullWidth'>
        <Tab label={`Não finalizados${isBusinessMode ? ` (${pending.length})` : ''}`} />
        <Tab label={`Finalizados${isBusinessMode ? ` (${completed.length})` : ''}`} />
      </Tabs>

      {tabIndex === 0 && (
        <AppointmentFinance appointments={pending} refetch={refetchAppointments} completed={false} />
      )}
      {tabIndex === 1 && (
        <AppointmentFinance appointments={completed} refetch={refetchAppointments} completed />
      )}

      {isBusinessMode && (
        <NewOrderModal
          business={business!}
          open={newOrderModalOpen}
          onClose={() => setNewOrderModalOpen(false)}
          onSuccess={() => refetchAppointments()}
        />
      )}
    </Box>
  );
};
